import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { concatLatestFrom } from '@ngrx/operators'
import { Store } from '@ngrx/store'
import { of, timer } from 'rxjs'
import { catchError, exhaustMap, filter, groupBy, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators'

import { FavoriteService } from '@app/modules/favorite/services/favorite.service'
import { MessagingService } from '@app/modules/messaging/services/messaging.service'
import { PromptService } from '@app/overlays/prompt/prompt.service'

import * as fromFeature from '.'
import * as fromProfile from '../profile'
import * as fromProfiles from '../profiles'
import type * as fromRoot from '../root'

@Injectable()
export class FavoriteEffects {
  private readonly actions$ = inject(Actions)
  private readonly store = inject<Store<fromRoot.State>>(Store)
  private readonly favoriteService = inject(FavoriteService)
  private readonly promptService = inject(PromptService)
  private readonly messagingService = inject(MessagingService)

  toggle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromFeature.Actions.toggle),
      groupBy(({ profileId }) => profileId),
      mergeMap(group =>
        group.pipe(
          exhaustMap(({ profileId, source }) => {
            const favorited = this.store.selectSignal(fromProfile.Selectors.isFavorited(profileId))()
            return timer(500).pipe(
              switchMap(() =>
                favorited
                  ? this.favoriteService
                      .add(profileId, source)
                      .pipe(map(() => fromProfile.Actions.favorited({ id: profileId })))
                  : this.favoriteService
                      .remove(profileId, source)
                      .pipe(map(() => fromProfile.Actions.unfavorited({ id: profileId }))),
              ),
              catchError(() => of(fromFeature.Actions.toggleFailure({ profileId, favorited }))),
              takeUntil(group),
            )
          }),
        ),
      ),
    ),
  )

  showPrompt$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromProfile.Actions.favorited),
        concatLatestFrom(({ id }) => [
          this.store.select(fromProfile.Selectors.hasAlreadyTalked(id)),
          this.store.select(fromProfiles.Selectors.profile(id)),
        ]),
        filter(([, hasAlreadyTalked]) => !hasAlreadyTalked && !this.messagingService.isAnyConversationOpen()),
        tap(([_, __, profile]) => {
          this.promptService.openFavorite(profile!)
        }),
      ),
    { dispatch: false },
  )
}
